import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Spinner } from "../components/Brand";

/**
 * Polls the Stripe status endpoint until the payment is confirmed as
 * `paid` (webhook + inline retrieve fallback both flip the DB). Stops after
 * ~30 seconds regardless — the webhook usually lands within 3s.
 */
export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "pending" });
    const [attempts, setAttempts] = useState(0);
    const timer = useRef(null);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;

        const poll = async (n = 0) => {
            try {
                const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
                if (cancelled) return;
                setState(data);
                setAttempts(n);
                if (data.payment_status === "paid") return;
                if (["expired", "failed"].includes(data.payment_status)) return;
                if (n >= 15) return; // ~30s
                timer.current = setTimeout(() => poll(n + 1), 2000);
            } catch {
                if (!cancelled && n < 15) timer.current = setTimeout(() => poll(n + 1), 2000);
            }
        };
        poll(0);
        return () => { cancelled = true; if (timer.current) clearTimeout(timer.current); };
    }, [sessionId]);

    const paid = state.payment_status === "paid";
    const failed = ["expired", "failed"].includes(state.payment_status);

    return (
        <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-white border border-[#EDE6D6] p-8 text-center shadow-sm"
                 data-testid="payment-success-card">
                {paid ? (
                    <>
                        <div className="text-5xl text-emerald-600 mb-4">✓</div>
                        <h1 className="font-serif text-3xl text-[#180A21] mb-2">Payment received</h1>
                        <p className="text-sm text-[#555] mb-6">
                            Thank you. Your booking is now confirmed and paid.
                        </p>
                        {state.booking_id && (
                            <button
                                data-testid="pay-success-view-booking"
                                onClick={() => nav(`/customer/bookings/${state.booking_id}`)}
                                className="lx-btn-gold px-6 py-3 text-sm"
                            >
                                View my booking
                            </button>
                        )}
                        <div className="mt-4">
                            <Link to="/customer/bookings" className="text-xs text-[#B2912A] hover:underline">
                                All bookings →
                            </Link>
                        </div>
                    </>
                ) : failed ? (
                    <>
                        <div className="text-5xl text-red-600 mb-4">×</div>
                        <h1 className="font-serif text-3xl text-[#180A21] mb-2">Payment not completed</h1>
                        <p className="text-sm text-[#555] mb-6">
                            The Stripe session expired or the payment failed. You can retry from your booking.
                        </p>
                        {state.booking_id && (
                            <button onClick={() => nav(`/customer/bookings/${state.booking_id}`)}
                                    className="lx-btn-gold px-6 py-3 text-sm">
                                Back to booking
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <Spinner className="mx-auto mb-4" />
                        <h1 className="font-serif text-2xl text-[#180A21] mb-2">Confirming your payment…</h1>
                        <p className="text-sm text-[#555]">
                            This usually takes a couple of seconds.
                            {attempts > 3 && " Still working — hang tight."}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
